import { useCallback, useInsertionEffect, useRef, useState } from 'react'

export type ShopifyScopeLifecycle = {
  token: string
  isActive: (token: string) => boolean
}

type ScopeGeneration = { scopeKey: string; generation: number }

/**
 * Gives async Shopify work a scope and lifecycle token.
 * A reconnect to the same scope gets a new token, and unmounted consumers
 * reject completions so late responses cannot update a replacement surface.
 *
 * The token follows COMMITTED renders only. The scope generation lives in state and is adjusted while
 * rendering when the scope changes (React's supported "adjust state on prop change" pattern), so a
 * render that React discards (for example a suspended transition) cannot advance it. The committed
 * token is published in an insertion effect, which runs before every layout and passive effect of the
 * commit, including child effects, so work started by any effect of that commit sees it as active.
 */
export function useShopifyScopeLifecycle(scopeKey: string): ShopifyScopeLifecycle {
  const [committedScope, setCommittedScope] = useState<ScopeGeneration>({ scopeKey, generation: 0 })
  let scope = committedScope
  if (committedScope.scopeKey !== scopeKey) {
    scope = { scopeKey, generation: committedScope.generation + 1 }
    setCommittedScope(scope)
  }
  const token = `${scope.scopeKey}:lifecycle-${scope.generation}`

  const activeTokenRef = useRef<string | null>(null)
  useInsertionEffect(() => {
    activeTokenRef.current = token
    return () => {
      activeTokenRef.current = null
    }
  }, [token])

  const isActive = useCallback((candidate: string) => activeTokenRef.current === candidate, [])
  return { token, isActive }
}
